import React, { useContext } from 'react';
import { Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import moment from 'moment';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';

/** What Excel accepts as a tab name, which is what the export truncates a longer table name to. */
export const MAX_SHEET_NAME_LENGTH = 31;

/**
 * What each sheet of the review workbook holds, in workbook order.
 *
 * Built from the table name rather than being a constant, because the first tab is named after the table (the
 * export names it `sheetName(tableName)`, truncated to what Excel accepts). Returned as data rather than markup
 * so the list is one thing to change when the workbook changes, and so a test can assert the wording against
 * the same source the dialog renders from.
 */
export function sheetsFor(tableName: string): { name: string; contents: string }[] {
    return [
        {
            // Truncated as the export truncates it, so the dialog names the tab a long-named table will
            // actually get. Blank is unreachable while Download waits for the definition; the spreadsheet
            // library names the tab itself when a table stores no name, so a generic word is the closest true
            // thing to say rather than an invented name.
            name: tableName ? tableName.slice(0, MAX_SHEET_NAME_LENGTH) : 'Data',
            contents: 'The period\'s active rows: Segment Value, Description and Period, then each configured '
                + 'column paired with its source type, and Totals and Unallocated footer rows.',
        },
        {
            name: 'Table Facts',
            contents: 'A Field / Value list of the table itself: name, id, segment type, owner, status, refresh '
                + 'cadence, period, segment population, created, last updated, validation status and last '
                + 'validated.',
        },
        {
            name: 'Metadata',
            contents: 'One row per configured column: column, source type, data type, currency, GL pull '
                + 'criteria, derivation, default value and period behaviour.',
        },
        {
            name: 'Validation',
            contents: 'One row per validation finding: rule, status, column, message and details.',
        },
    ];
}

/** `yyyyMM` as the mock words a period (`JUN-2026`); anything else passes through rather than being blanked. */
export function formatDownloadPeriod(period?: string): string {
    if (!period || !/^\d{6}$/.test(period)) {
        return period ?? '';
    }
    return moment(period, 'YYYYMM').format('MMM-YYYY').toUpperCase();
}

/**
 * Confirmation shown before the review workbook is fetched, so a user knows what they are about to get.
 *
 * <p>The download is otherwise silent and slow: the button fires a request for a pre-signed URL and the file
 * lands in the browser's downloads with no statement anywhere of which period it covers or what is in it. For
 * a read-only artefact circulated for review, that is the thing a reviewer most needs to be told up front.
 *
 * <p>Reads the table name and the selected period from context rather than taking them as props, matching
 * `SegmentUploadModal` and the rest of this folder; only visibility and the two actions are props, since the
 * fetch and the anchor-click that follow it live in the toolbar.
 *
 * <p><b>Deliberately states no file name.</b> `DownloadSegmentDataTable` returns a pre-signed URL and nothing
 * else, and the name the workbook is stored under is composed server-side from the table id, the requesting
 * user and a request timestamp (`DownloadSegmentDataTableComponent.fileName`). Anything shown here would be a
 * guess that does not match what arrives, so the dialog states the format and leaves the name to the browser.
 */
export default function SegmentDownloadModal(props: {
    visible: boolean;
    downloading?: boolean;
    onDismiss: () => void;
    onConfirm: () => void;
}) {
    const { visible, downloading, onDismiss, onConfirm } = props;
    const context = useContext(SegmentTablesContext);
    const detail = context?.state?.detail;
    // The table name comes from the definition, so it is absent until that GET resolves. The toolbar only opens
    // this once a period is selected, and it disables Download until the definition has loaded.
    const tableName = detail?.definition?.tableName ?? '';
    const period = formatDownloadPeriod(detail?.selectedPeriod);

    if (!visible) {
        return null;
    }

    return (
        <Modal
            visible
            onDismiss={onDismiss}
            size="medium"
            closeAriaLabel="Close download dialog"
            header="Download review file"
            footer={(
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={onDismiss}>Cancel</Button>
                        <Button variant="primary" loading={downloading} disabled={downloading} onClick={onConfirm}>
                            Download
                        </Button>
                    </SpaceBetween>
                </Box>
            )}
        >
            <SpaceBetween size="m">
                <Box>
                    This downloads an Excel review file for <strong>{tableName}</strong>, period{' '}
                    <strong>{period}</strong>. It contains:
                </Box>

                {/* A real table, so the sheet number and its contents stay aligned as the descriptions wrap. */}
                <table className="segmentDownloadSheets">
                    <tbody>
                        {sheetsFor(tableName).map((sheet, i) => (
                            <tr key={sheet.name}>
                                <td className="segmentDownloadSheetIndex"><strong>Sheet {i + 1}</strong></td>
                                <td>
                                    <Box variant="strong">{sheet.name}</Box>
                                    <Box color="text-body-secondary">{sheet.contents}</Box>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Box color="text-body-secondary" fontSize="body-s">
                    Format: .xlsx &middot; Read-only (review only)
                </Box>
            </SpaceBetween>
        </Modal>
    );
}
